/*
 * [Rarist] <menu-scroll-arrow>: strzałka przewijania menu bocznego (snippets/menu-scroll-arrow.liquid).
 *
 * - Przewija się .menu-drawer__panel-body aktualnie widocznego panelu (header-menu-drawer-panel.is-visible): głównego
 *   albo podmenu. ::part(content) menu ma overflow: clip. Gdyby układ się zmienił, findScroller() szuka też innych
 *   kontenerów z overflow-y: auto/scroll (panel, ::part(body) i ::part(content) w shadow DOM).
 * - Strzałka jest widoczna (klasa is-active), gdy treść panelu bez dolnego paddingu nie mieści się w widocznej części.
 * - Na samym dole (klasa is-at-bottom) strzałka obraca się w górę i przewija na samą górę; po zjechaniu z dołu wraca
 *   w dół. Histereza (BOTTOM_ENTER / BOTTOM_LEAVE) zapobiega miganiu przy ułamkowych wartościach scrollTop.
 * - Przeliczanie: otwarcie menu (dialog:before-show i dialog:after-show), przewijanie, zmiana rozmiaru (ResizeObserver)
 *   i przełączanie paneli podmenu (MutationObserver na klasie is-visible paneli).
 * - Płynne przewijanie (własna animacja, patrz animateScroll) tylko po kliknięciu strzałki; przy prefers-reduced-motion przewijanie jest
 *   natychmiastowe, jak animacje samego motywu. Reset przewinięcia przy otwarciu menu robi
 *   osobny skrypt w snippets/main-menu-drawer.liquid (natychmiast, w dialog:before-show); tu tylko przeliczamy stan.
 * - Pojawia się i znika razem z pozycjami menu: przy otwarciu ta sama animacja wejścia co pozycje (onBeforeShow), przy
 *   zamknięciu theme.js wygasza ją razem z całą zawartością slotu.
 * - Wszystkie nasłuchy i obserwatory są zdejmowane w disconnectedCallback (przerysowanie sekcji w edytorze motywu).
 */
const SCROLL_RATIO = 0.85; // krok przy długim menu = 85% widocznej wysokości panelu (zakładka 15%)
const SNAP_TO_END_RATIO = 0.25; // gdy po kroku zostałoby mniej niż 25% ekranu, od razu na sam dół
const OVERFLOW_MIN = 4; // tyle px treści (bez dolnego paddingu) musi się nie mieścić, żeby pokazać strzałkę
const BOTTOM_ENTER = 2; // "na samym dole": do końca przewijania zostały najwyżej 2 px
const BOTTOM_LEAVE = 12; // strzałka wraca w dół dopiero, gdy do końca jest więcej niż 12 px
const SCROLL_MIN_DURATION = 350; // ms – czas animacji przewijania zależy od odległości, w tych granicach
const SCROLL_MAX_DURATION = 900;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class MenuScrollArrow extends HTMLElement {
  connectedCallback() {
    this.cleanup();

    this.drawer = this.closest('header-menu-drawer');
    this.button = this.querySelector('.menu-scroll-arrow__button');

    if (!this.drawer || !this.button) {
      return;
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.scroller = null;
    this.activePanel = null;
    this.isAtBottom = false;

    this.button.addEventListener('click', () => this.onClick(), { signal });
    this.drawer.addEventListener('dialog:before-show', () => this.onBeforeShow(), { signal });
    this.drawer.addEventListener('dialog:after-show', () => this.requestUpdate(), { signal });
    this.drawer.addEventListener('dialog:after-hide', () => this.update(), { signal });

    this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
    this.panelObserver = new MutationObserver((records) => this.onPanelsMutated(records));

    this.drawer.querySelectorAll('header-menu-drawer-panel').forEach((panel) => {
      this.panelObserver.observe(panel, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
    });

    this.update();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    this.abortController?.abort();
    this.scrollerAbortController?.abort();
    this.resizeObserver?.disconnect();
    this.panelObserver?.disconnect();
    cancelAnimationFrame(this.frameRequest || 0);
    this.enterAnimation?.cancel();
    this.cancelScrollAnimation();

    this.abortController = this.scrollerAbortController = this.resizeObserver = this.panelObserver = null;
    this.frameRequest = 0;
    this.scroller = null;
  }

  onBeforeShow() {
    // Przy zamykaniu theme.js wygasza wszystkie elementy slotu (także ten) i zostawia im inline opacity: 0
    this.style.removeProperty('opacity');
    this.isAtBottom = false;
    this.requestUpdate();

    // Wejście razem z pozycjami menu: theme.js (HeaderMenuDrawerPanel.show) pokazuje je tak samo – opacity 0 → 1
    // i translateY(1em) → 0 w 0,2 s, pierwsza po 0,4 s. Przy ograniczonym ruchu motyw nie animuje pozycji, więc tu też nie.
    this.enterAnimation?.cancel();

    if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      this.enterAnimation = this.animate(
        [{ opacity: 0, transform: 'translateY(1em)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 200, delay: 400, easing: 'ease', fill: 'backwards' }
      );
    }
  }

  onPanelsMutated(records) {
    records.forEach((record) => {
      const wasVisible = (record.oldValue || '').split(/\s+/).includes('is-visible');

      // Panel, który właśnie się pokazał, jest docelowym (podczas przejścia oba panele mają is-visible)
      if (!wasVisible && record.target.classList.contains('is-visible')) {
        this.activePanel = record.target;
      }
    });

    this.requestUpdate();
  }

  onClick() {
    const scroller = this.scroller;

    if (!scroller) {
      return;
    }

    const behavior = reducedMotion.matches ? 'auto' : 'smooth';

    const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const remaining = maxScroll - scroller.scrollTop;
    let target = 0;

    if (!this.isAtBottom) {
      // Gdy reszta treści mieści się na jednym ekranie, od razu na sam dół (nic nie zostaje pominięte). Dłuższe menu:
      // krok z zakładką, a gdy po nim zostałby tylko kawałek, też od razu na dół.
      const step = Math.round(scroller.clientHeight * SCROLL_RATIO);
      target = remaining <= scroller.clientHeight || remaining - step < scroller.clientHeight * SNAP_TO_END_RATIO ? maxScroll : scroller.scrollTop + step;
    }
    this.animateScroll(scroller, target, behavior === 'smooth');
  }

  /**
   * Własna animacja przewijania zamiast scrollBy/scrollTo({ behavior: 'smooth' }): w iOS Safari natywne płynne
   * przewijanie wewnątrz przewijanego elementu szarpie, potrafi przestrzelić koniec treści (pusta przestrzeń pod menu)
   * i nie kończy się dokładnie na dole, więc strzałka się nie obracała. Tu cel jest zawsze w zakresie 0…maks.,
   * a po zakończeniu stan strzałki jest przeliczany. Dotknięcie lub kółko myszy przerywa animację.
   */
  animateScroll(scroller, target, smooth) {
    this.cancelScrollAnimation();

    const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const start = scroller.scrollTop;
    const end = Math.min(Math.max(0, target), maxScroll);
    const distance = end - start;

    if (!smooth || Math.abs(distance) < 1) {
      scroller.scrollTop = end;
      this.update();
      return;
    }

    // CSS scroll-behavior: smooth wygładzałby każdy krok drugi raz
    const previousBehavior = scroller.style.getPropertyValue('scroll-behavior');
    scroller.style.setProperty('scroll-behavior', 'auto', 'important');

    const duration = Math.min(SCROLL_MAX_DURATION, Math.max(SCROLL_MIN_DURATION, Math.abs(distance) * 0.9));
    const startTime = performance.now();

    const finish = () => {
      this.scrollFrame = 0;
      this.scrollAnimationCleanup = null;

      if (previousBehavior) {
        scroller.style.setProperty('scroll-behavior', previousBehavior);
      } else {
        scroller.style.removeProperty('scroll-behavior');
      }
    };

    const step = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2; // easeInOutCubic

      scroller.scrollTop = start + distance * eased;

      if (progress < 1) {
        this.scrollFrame = requestAnimationFrame(step);
      } else {
        finish();
        this.update();
      }
    };

    this.scrollAnimationCleanup = finish;
    this.scrollFrame = requestAnimationFrame(step);
  }

  cancelScrollAnimation() {
    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollAnimationCleanup?.();
      this.scrollFrame = 0;
    }
  }

  get visiblePanel() {
    const visiblePanels = Array.from(this.drawer.querySelectorAll('header-menu-drawer-panel.is-visible'));

    if (this.activePanel && visiblePanels.includes(this.activePanel)) {
      return this.activePanel;
    }

    return visiblePanels[visiblePanels.length - 1] || null;
  }

  findScroller(panel) {
    const shadowRoot = this.drawer.shadowRoot;
    const candidates = [
      panel.querySelector('.menu-drawer__panel-body'),
      panel,
      shadowRoot?.querySelector('[part~="body"]'),
      shadowRoot?.querySelector('[part~="content"]'),
    ];

    return candidates.find((element) => element && /auto|scroll/.test(getComputedStyle(element).overflowY)) || null;
  }

  setScroller(scroller) {
    if (scroller === this.scroller) {
      return;
    }

    this.cancelScrollAnimation();
    this.scrollerAbortController?.abort();
    this.resizeObserver.disconnect();
    this.scroller = scroller;

    if (!scroller) {
      return;
    }

    this.scrollerAbortController = new AbortController();
    scroller.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal: this.scrollerAbortController.signal });

    // Przewijanie palcem lub kółkiem w trakcie animacji strzałki: animacja ustępuje użytkownikowi
    ['touchstart', 'wheel'].forEach((type) => {
      scroller.addEventListener(type, () => this.cancelScrollAnimation(), { passive: true, signal: this.scrollerAbortController.signal });
    });

    // Rozmiar kontenera (np. obrót telefonu) i jego treści (czcionki, obrazki w promo)
    this.resizeObserver.observe(scroller);
    Array.from(scroller.children).forEach((child) => this.resizeObserver.observe(child));
  }

  requestUpdate() {
    if (!this.frameRequest) {
      this.frameRequest = requestAnimationFrame(() => {
        this.frameRequest = 0;
        this.update();
      });
    }
  }

  update() {
    if (!this.drawer) {
      return;
    }

    const panel = this.drawer.hasAttribute('open') ? this.visiblePanel : null;
    this.setScroller(panel ? this.findScroller(panel) : null);

    const scroller = this.scroller;
    let isActive = false;

    if (scroller && scroller.clientHeight > 0) {
      const maxScroll = scroller.scrollHeight - scroller.clientHeight;
      const style = getComputedStyle(scroller);
      const paddingEnd = parseFloat(style.paddingBlockEnd || style.paddingBottom) || 0;
      const remaining = maxScroll - scroller.scrollTop;

      isActive = maxScroll - paddingEnd > OVERFLOW_MIN;
      this.isAtBottom = isActive && remaining <= (this.isAtBottom ? BOTTOM_LEAVE : BOTTOM_ENTER);
    } else {
      this.isAtBottom = false;
    }

    this.classList.toggle('is-active', isActive);
    this.classList.toggle('is-at-bottom', this.isAtBottom);

    const label = this.isAtBottom ? this.dataset.labelUp : this.dataset.labelDown;

    if (label && this.button.getAttribute('aria-label') !== label) {
      this.button.setAttribute('aria-label', label);
    }
  }
}

if (!window.customElements.get('menu-scroll-arrow')) {
  window.customElements.define('menu-scroll-arrow', MenuScrollArrow);
}
